import {
  CRAFT_GRID_SIZE_OPTIONS,
  STANDARD_BLOCK_TYPES,
  type VoxelMaterial,
} from '@shared/protocol';

const FORMAT_VERSION = 1;
const MAX_FORMAT_SIZE = 64;
const HEX6 = /^#[0-9a-f]{6}$/i;
const HEX8 = /^#[0-9a-f]{8}$/i;
/** `#rrggbbaagg` — alpha + glow intensity byte. */
const HEX10 = /^#[0-9a-f]{10}$/i;
const PALETTE_ID = /^[0a-zA-Z]$/;
const SPARSE_LINE = /^(\d+)\s*,\s*(\d+)\s+(\S+)\s*$/;

export interface CustomBlockFile {
  name: string;
  gridSize: number;
  voxels: Array<[number, VoxelMaterial]>;
}

/** Serialize a custom block to BFCB text (see related/formats/bfcb.md). */
export function serializeCustomBlockFile(
  name: string,
  gridSize: number,
  voxels: Array<[number, VoxelMaterial]>,
): string {
  const materials = uniqueMaterials(voxels);
  const palette = buildPalette(materials);
  const lines: string[] = [
    `bfcb ${FORMAT_VERSION}`,
    `name ${name.trim() || 'Custom block'}`,
    `size ${gridSize}`,
    '',
  ];
  if (palette.entries.length > 0) {
    lines.push('palette');
    for (const [id, material] of palette.entries) {
      lines.push(`  ${id} ${formatMaterial(material)}`);
    }
    lines.push('');
  }

  const byLayer = new Map<number, Array<{ x: number; z: number; material: VoxelMaterial }>>();
  for (const [index, material] of voxels) {
    const x = index % gridSize;
    const z = Math.floor(index / gridSize) % gridSize;
    const y = Math.floor(index / (gridSize * gridSize));
    const list = byLayer.get(y) ?? [];
    list.push({ x, z, material });
    byLayer.set(y, list);
  }

  const layers = [...byLayer.keys()].sort((a, b) => a - b);
  for (const y of layers) {
    const cells = byLayer.get(y)!;
    lines.push(`y ${y}`);
    const useDense = cells.length >= Math.max(4, (gridSize * gridSize) / 8);
    if (useDense) {
      const grid = Array.from({ length: gridSize }, () => Array(gridSize).fill('.'));
      for (const cell of cells) {
        grid[cell.z][cell.x] = palette.keyFor(cell.material);
      }
      for (const row of grid) lines.push(row.join(''));
    } else {
      cells
        .sort((a, b) => a.z - b.z || a.x - b.x)
        .forEach((cell) => {
          lines.push(`  ${cell.x},${cell.z} ${palette.keyFor(cell.material)}`);
        });
    }
    lines.push('');
  }
  return `${lines.join('\n').trimEnd()}\n`;
}

export function downloadCustomBlockFile(
  name: string,
  gridSize: number,
  voxels: Array<[number, VoxelMaterial]>,
): void {
  const text = serializeCustomBlockFile(name, gridSize, voxels);
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${sanitizeFilename(name) || 'custom-block'}.bfcb`;
  link.click();
  URL.revokeObjectURL(link.href);
}

export async function customBlockFileFromTextFile(file: File): Promise<CustomBlockFile> {
  const text = await file.text();
  return parseCustomBlockFile(text);
}

/** Parse BFCB text into name / gridSize / sparse voxels. */
export function parseCustomBlockFile(text: string): CustomBlockFile {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/);
  let version: number | null = null;
  let name = '';
  let gridSize: number | null = null;
  const palette = new Map<string, VoxelMaterial>();
  const voxels = new Map<number, VoxelMaterial>();

  let mode: 'root' | 'palette' | 'layer' = 'root';
  let layerY = 0;
  let layerStyle: 'dense' | 'sparse' | null = null;
  let denseRows: string[] = [];

  const flushDense = () => {
    if (layerStyle !== 'dense') return;
    if (gridSize === null) throw new Error('Missing size before layer data');
    if (denseRows.length !== gridSize) {
      throw new Error(
        `Layer y ${layerY}: expected ${gridSize} dense rows, got ${denseRows.length}`,
      );
    }
    denseRows.forEach((row, z) => {
      if (row.length !== gridSize) {
        throw new Error(
          `Layer y ${layerY}, row ${z}: expected ${gridSize} columns, got ${row.length}`,
        );
      }
      for (let x = 0; x < gridSize!; x++) {
        const ch = row[x];
        if (ch === '.') continue;
        const material = resolveToken(ch, palette, true);
        voxels.set(indexAt(x, layerY, z, gridSize!), material);
      }
    });
    denseRows = [];
    layerStyle = null;
  };

  for (let lineNo = 0; lineNo < lines.length; lineNo++) {
    const raw = lines[lineNo];
    const trimmed = raw.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;

    if (mode === 'palette') {
      if (/^(bfcb|name|size|palette|y)\b/.test(trimmed)) {
        mode = 'root';
        // fall through to root handling
      } else {
        const match = trimmed.match(/^(\S+)\s+(\S+)$/);
        if (!match || !PALETTE_ID.test(match[1])) {
          throw new Error(`Invalid palette entry on line ${lineNo + 1}`);
        }
        palette.set(match[1], parseMaterialToken(match[2]));
        continue;
      }
    }

    if (mode === 'layer') {
      if (/^(bfcb|name|size|palette|y)\b/.test(trimmed)) {
        flushDense();
        mode = 'root';
        // fall through
      } else {
        if (layerStyle === null) {
          layerStyle = SPARSE_LINE.test(trimmed) ? 'sparse' : 'dense';
        }
        if (layerStyle === 'sparse') {
          const match = trimmed.match(SPARSE_LINE);
          if (!match) throw new Error(`Invalid sparse voxel on line ${lineNo + 1}`);
          if (gridSize === null) throw new Error('Missing size before layer data');
          const x = Number(match[1]);
          const z = Number(match[2]);
          if (x < 0 || z < 0 || x >= gridSize || z >= gridSize) {
            throw new Error(`Voxel out of bounds on line ${lineNo + 1}`);
          }
          const material = resolveToken(match[3], palette, false);
          voxels.set(indexAt(x, layerY, z, gridSize), material);
        } else {
          denseRows.push(trimmed);
        }
        continue;
      }
    }

    // root
    if (trimmed.startsWith('bfcb ')) {
      version = Number(trimmed.slice(5).trim());
      if (version !== FORMAT_VERSION) {
        throw new Error(`Unsupported bfcb version ${trimmed.slice(5).trim()}`);
      }
      continue;
    }
    if (trimmed.startsWith('name ')) {
      name = trimmed.slice(5).trim();
      continue;
    }
    if (trimmed.startsWith('size ')) {
      const size = Number(trimmed.slice(5).trim());
      if (!Number.isInteger(size) || size < 1 || size > MAX_FORMAT_SIZE) {
        throw new Error(`Invalid size (use 1..${MAX_FORMAT_SIZE})`);
      }
      gridSize = size;
      continue;
    }
    if (trimmed === 'palette') {
      mode = 'palette';
      continue;
    }
    if (trimmed.startsWith('y ')) {
      if (version === null) throw new Error('Missing bfcb version header');
      if (gridSize === null) throw new Error('Missing size before layers');
      flushDense();
      layerY = Number(trimmed.slice(2).trim());
      if (!Number.isInteger(layerY) || layerY < 0 || layerY >= gridSize) {
        throw new Error(`Invalid layer y on line ${lineNo + 1}`);
      }
      mode = 'layer';
      layerStyle = null;
      denseRows = [];
      continue;
    }
    throw new Error(`Unexpected content on line ${lineNo + 1}: ${trimmed}`);
  }
  flushDense();

  if (version === null) throw new Error('Missing bfcb version header');
  if (gridSize === null) throw new Error('Missing size');
  if (voxels.size === 0) throw new Error('File has no voxels');
  if (!(CRAFT_GRID_SIZE_OPTIONS as readonly number[]).includes(gridSize)) {
    throw new Error(
      `BlankForge currently imports sizes ${CRAFT_GRID_SIZE_OPTIONS.join(', ')} only ` +
        `(file has size ${gridSize})`,
    );
  }
  return {
    name: name || 'Custom block',
    gridSize,
    voxels: [...voxels.entries()].sort((a, b) => a[0] - b[0]),
  };
}

function indexAt(x: number, y: number, z: number, gridSize: number): number {
  return x + z * gridSize + y * gridSize * gridSize;
}

function parseMaterialToken(token: string): VoxelMaterial {
  if (/^\d+$/.test(token)) {
    const id = Number(token);
    if (!Number.isInteger(id) || id < 1 || id > STANDARD_BLOCK_TYPES) {
      throw new Error(`Unknown standard type ${token}`);
    }
    return id;
  }
  const hex = token.toLowerCase();
  if (HEX10.test(hex) || HEX8.test(hex)) return hex;
  if (HEX6.test(hex)) return `${hex}ff`;
  throw new Error(`Invalid material “${token}”`);
}

function resolveToken(
  token: string,
  palette: Map<string, VoxelMaterial>,
  denseChar: boolean,
): VoxelMaterial {
  if (denseChar && token.length === 1 && token >= '1' && token <= '9') {
    return Number(token);
  }
  if (palette.has(token)) return palette.get(token)!;
  if (!denseChar) return parseMaterialToken(token);
  throw new Error(`Unknown palette id “${token}” (define it under palette)`);
}

function formatMaterial(material: VoxelMaterial): string {
  return typeof material === 'number' ? String(material) : material.toLowerCase();
}

function uniqueMaterials(voxels: Array<[number, VoxelMaterial]>): VoxelMaterial[] {
  const seen = new Set<string>();
  const list: VoxelMaterial[] = [];
  for (const [, material] of voxels) {
    const key = typeof material === 'number' ? `n:${material}` : `c:${material.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    list.push(typeof material === 'number' ? material : material.toLowerCase());
  }
  return list;
}

function buildPalette(materials: VoxelMaterial[]): {
  entries: Array<[string, VoxelMaterial]>;
  keyFor(material: VoxelMaterial): string;
} {
  const keyByMaterial = new Map<string, string>();
  const entries: Array<[string, VoxelMaterial]> = [];
  const ids = paletteIdStream();

  for (const material of materials) {
    const storageKey = typeof material === 'number'
      ? `n:${material}`
      : `c:${material.toLowerCase()}`;
    if (typeof material === 'number' && material >= 1 && material <= 9) {
      keyByMaterial.set(storageKey, String(material));
      continue;
    }
    const id = ids.next().value!;
    keyByMaterial.set(storageKey, id);
    entries.push([id, material]);
  }

  return {
    entries,
    keyFor(material: VoxelMaterial) {
      const storageKey = typeof material === 'number'
        ? `n:${material}`
        : `c:${material.toLowerCase()}`;
      return keyByMaterial.get(storageKey) ?? '?';
    },
  };
}

function* paletteIdStream(): Generator<string> {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0';
  for (const ch of chars) yield ch;
  throw new Error('Too many distinct materials for dense palette ids');
}

function sanitizeFilename(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9-_]+/g, '-').replace(/^-|-$/g, '');
}
